import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Page,
  Layout,
  Card,
  FormLayout,
  TextField,
  Select,
  Checkbox,
  Banner,
  Toast,
  BlockStack,
  Text,
} from "@shopify/polaris";
import { useVendors, type CategoryMinLevel } from "../hooks/useVendors";
import CategoryMinLevelsEditor from "../components/CategoryMinLevelsEditor";

const PROVINCES = [
  { label: "Select province", value: "" },
  { label: "Alberta", value: "AB" },
  { label: "British Columbia", value: "BC" },
  { label: "Manitoba", value: "MB" },
  { label: "New Brunswick", value: "NB" },
  { label: "Newfoundland and Labrador", value: "NL" },
  { label: "Northwest Territories", value: "NT" },
  { label: "Nova Scotia", value: "NS" },
  { label: "Nunavut", value: "NU" },
  { label: "Ontario", value: "ON" },
  { label: "Prince Edward Island", value: "PE" },
  { label: "Quebec", value: "QC" },
  { label: "Saskatchewan", value: "SK" },
  { label: "Yukon", value: "YT" },
];

interface FormState {
  name: string;
  code: string;
  accountNumber: string;
  contactName: string;
  email: string;
  orderEmail: string;
  fax: string;
  phone: string;
  website: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
  mailingAddressLine1: string;
  mailingAddressLine2: string;
  mailingCity: string;
  mailingProvince: string;
  mailingPostalCode: string;
  mailingCountry: string;
  discountPercent: string;
  paymentTerms: string;
  leadTimeDays: string;
  notes: string;
  isActive: boolean;
}

const EMPTY_FORM: FormState = {
  name: "",
  code: "",
  accountNumber: "",
  contactName: "",
  email: "",
  orderEmail: "",
  fax: "",
  phone: "",
  website: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  province: "ON",
  postalCode: "",
  country: "Canada",
  mailingAddressLine1: "",
  mailingAddressLine2: "",
  mailingCity: "",
  mailingProvince: "",
  mailingPostalCode: "",
  mailingCountry: "Canada",
  discountPercent: "0",
  paymentTerms: "",
  leadTimeDays: "14",
  notes: "",
  isActive: true,
};

export default function VendorCreate() {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
  const isEditing = !!vendorId;

  const { loadVendor, createVendor, updateVendor, updateCategoryMinLevels, error } =
    useVendors();

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [categoryLevels, setCategoryLevels] = useState<CategoryMinLevel[]>([]);
  const [saving, setSaving] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  useEffect(() => {
    if (isEditing) {
      loadVendor(vendorId).then((vendor) => {
        if (vendor) {
          setForm({
            name: vendor.name || "",
            code: vendor.code || "",
            accountNumber: vendor.accountNumber || "",
            contactName: vendor.contactName || "",
            email: vendor.email || "",
            orderEmail: vendor.orderEmail || "",
            fax: vendor.fax || "",
            phone: vendor.phone || "",
            website: vendor.website || "",
            addressLine1: vendor.addressLine1 || "",
            addressLine2: vendor.addressLine2 || "",
            city: vendor.city || "",
            province: vendor.province || "ON",
            postalCode: vendor.postalCode || "",
            country: vendor.country || "Canada",
            mailingAddressLine1: vendor.mailingAddressLine1 || "",
            mailingAddressLine2: vendor.mailingAddressLine2 || "",
            mailingCity: vendor.mailingCity || "",
            mailingProvince: vendor.mailingProvince || "",
            mailingPostalCode: vendor.mailingPostalCode || "",
            mailingCountry: vendor.mailingCountry || "Canada",
            discountPercent: String(vendor.discountPercent || 0),
            paymentTerms: vendor.paymentTerms || "",
            leadTimeDays: String(vendor.leadTimeDays || 14),
            notes: vendor.notes || "",
            isActive: vendor.isActive,
          });
          setCategoryLevels(vendor.categoryMinLevels || []);
        }
      });
    }
  }, [vendorId, isEditing, loadVendor]);

  const handleChange = useCallback(
    (field: keyof FormState) => (value: string | boolean) => {
      setForm((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const validate = (): boolean => {
    const errors: string[] = [];
    if (!form.name.trim()) errors.push("Vendor name is required");
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errors.push("Invalid email address");
    }
    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleSave = useCallback(async () => {
    if (!validate()) return;
    setSaving(true);

    const data = {
      name: form.name.trim(),
      code: form.code.trim() || undefined,
      accountNumber: form.accountNumber.trim() || undefined,
      contactName: form.contactName.trim() || undefined,
      email: form.email.trim() || undefined,
      orderEmail: form.orderEmail.trim() || undefined,
      fax: form.fax.trim() || undefined,
      phone: form.phone.trim() || undefined,
      website: form.website.trim() || undefined,
      addressLine1: form.addressLine1.trim() || undefined,
      addressLine2: form.addressLine2.trim() || undefined,
      city: form.city.trim() || undefined,
      province: form.province || undefined,
      postalCode: form.postalCode.trim() || undefined,
      country: form.country || undefined,
      mailingAddressLine1: form.mailingAddressLine1.trim() || undefined,
      mailingAddressLine2: form.mailingAddressLine2.trim() || undefined,
      mailingCity: form.mailingCity.trim() || undefined,
      mailingProvince: form.mailingProvince || undefined,
      mailingPostalCode: form.mailingPostalCode.trim() || undefined,
      mailingCountry: form.mailingCountry || undefined,
      discountPercent: parseFloat(form.discountPercent) || 0,
      paymentTerms: form.paymentTerms.trim() || undefined,
      leadTimeDays: parseInt(form.leadTimeDays) || 14,
      notes: form.notes.trim() || undefined,
      isActive: form.isActive,
    };

    let result;
    if (isEditing) {
      result = await updateVendor(vendorId, data);
      if (result) {
        await updateCategoryMinLevels(vendorId, categoryLevels);
        setToastMessage("Vendor updated");
      }
    } else {
      result = await createVendor({
        ...data,
        categoryMinLevels: categoryLevels,
      });
      if (result) {
        setToastMessage("Vendor created");
        navigate(`/vendors/${result.id}`);
      }
    }

    setSaving(false);
  }, [
    form,
    categoryLevels,
    isEditing,
    vendorId,
    createVendor,
    updateVendor,
    updateCategoryMinLevels,
    navigate,
  ]);

  return (
    <>
      <Page
        title={isEditing ? `Edit ${form.name}` : "New Vendor"}
        backAction={{ content: "Back", url: isEditing ? `/vendors/${vendorId}` : "/" }}
        primaryAction={{
          content: "Save",
          onAction: handleSave,
          loading: saving,
        }}
      >
        {validationErrors.length > 0 && (
          <Layout.Section>
            <Banner tone="critical">
              <ul>
                {validationErrors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </Banner>
          </Layout.Section>
        )}

        {error && (
          <Layout.Section>
            <Banner tone="critical">
              <p>{error}</p>
            </Banner>
          </Layout.Section>
        )}

        <Layout>
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">Basic Info</Text>
                <FormLayout>
                  <FormLayout.Group>
                    <TextField
                      label="Distributor Name"
                      value={form.name}
                      onChange={handleChange("name")}
                      requiredIndicator
                      autoComplete="off"
                      helpText="Must match the vendor name on Shopify products exactly"
                    />
                    <TextField
                      label="Code"
                      value={form.code}
                      onChange={handleChange("code")}
                      autoComplete="off"
                      helpText="Short code for PO numbering"
                    />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <TextField
                      label="Account Number"
                      value={form.accountNumber}
                      onChange={handleChange("accountNumber")}
                      autoComplete="off"
                      helpText="Your account # with this distributor"
                    />
                    <TextField
                      label="Website"
                      value={form.website}
                      onChange={handleChange("website")}
                      autoComplete="off"
                      placeholder="https://..."
                    />
                  </FormLayout.Group>
                  <Checkbox
                    label="Active"
                    checked={form.isActive}
                    onChange={handleChange("isActive")}
                  />
                </FormLayout>
              </BlockStack>
            </Card>
          </Layout.Section>

          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">Contact</Text>
                <FormLayout>
                  <TextField
                    label="Contact Name"
                    value={form.contactName}
                    onChange={handleChange("contactName")}
                    autoComplete="off"
                  />
                  <FormLayout.Group>
                    <TextField
                      label="Email"
                      type="email"
                      value={form.email}
                      onChange={handleChange("email")}
                      autoComplete="off"
                      helpText="General contact email"
                    />
                    <TextField
                      label="Phone"
                      value={form.phone}
                      onChange={handleChange("phone")}
                      autoComplete="off"
                    />
                  </FormLayout.Group>
                  <TextField
                    label="Order Email(s)"
                    value={form.orderEmail}
                    onChange={handleChange("orderEmail")}
                    autoComplete="off"
                    helpText="POs will be sent here. Separate multiple with commas."
                    placeholder="orders@distributor.com, rep@distributor.com"
                  />
                  <TextField
                    label="Fax"
                    value={form.fax}
                    onChange={handleChange("fax")}
                    autoComplete="off"
                  />
                </FormLayout>
              </BlockStack>
            </Card>
          </Layout.Section>

          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">Main Address</Text>
                <FormLayout>
                  <TextField
                    label="Address Line 1"
                    value={form.addressLine1}
                    onChange={handleChange("addressLine1")}
                    autoComplete="off"
                  />
                  <TextField
                    label="Address Line 2"
                    value={form.addressLine2}
                    onChange={handleChange("addressLine2")}
                    autoComplete="off"
                  />
                  <FormLayout.Group>
                    <TextField
                      label="City"
                      value={form.city}
                      onChange={handleChange("city")}
                      autoComplete="off"
                    />
                    <Select
                      label="Province"
                      options={PROVINCES}
                      value={form.province}
                      onChange={handleChange("province")}
                    />
                    <TextField
                      label="Postal Code"
                      value={form.postalCode}
                      onChange={handleChange("postalCode")}
                      autoComplete="off"
                    />
                  </FormLayout.Group>
                </FormLayout>
              </BlockStack>
            </Card>
          </Layout.Section>

          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">Mailing Address</Text>
                <Text as="p" tone="subdued">Where cheques are mailed. Leave blank if same as main address.</Text>
                <FormLayout>
                  <TextField
                    label="Address Line 1"
                    value={form.mailingAddressLine1}
                    onChange={handleChange("mailingAddressLine1")}
                    autoComplete="off"
                  />
                  <TextField
                    label="Address Line 2"
                    value={form.mailingAddressLine2}
                    onChange={handleChange("mailingAddressLine2")}
                    autoComplete="off"
                  />
                  <FormLayout.Group>
                    <TextField
                      label="City"
                      value={form.mailingCity}
                      onChange={handleChange("mailingCity")}
                      autoComplete="off"
                    />
                    <Select
                      label="Province"
                      options={PROVINCES}
                      value={form.mailingProvince}
                      onChange={handleChange("mailingProvince")}
                    />
                    <TextField
                      label="Postal Code"
                      value={form.mailingPostalCode}
                      onChange={handleChange("mailingPostalCode")}
                      autoComplete="off"
                    />
                  </FormLayout.Group>
                </FormLayout>
              </BlockStack>
            </Card>
          </Layout.Section>

          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">Terms</Text>
                <FormLayout>
                  <FormLayout.Group>
                    <TextField
                      label="Vendor Discount %"
                      type="number"
                      value={form.discountPercent}
                      onChange={handleChange("discountPercent")}
                      suffix="%"
                      autoComplete="off"
                    />
                    <TextField
                      label="Payment Terms"
                      value={form.paymentTerms}
                      onChange={handleChange("paymentTerms")}
                      placeholder="e.g., Net 30"
                      autoComplete="off"
                    />
                    <TextField
                      label="Lead Time (days)"
                      type="number"
                      value={form.leadTimeDays}
                      onChange={handleChange("leadTimeDays")}
                      autoComplete="off"
                    />
                  </FormLayout.Group>
                  <TextField
                    label="Notes"
                    value={form.notes}
                    onChange={handleChange("notes")}
                    multiline={3}
                    autoComplete="off"
                  />
                </FormLayout>
              </BlockStack>
            </Card>
          </Layout.Section>

          <Layout.Section>
            <CategoryMinLevelsEditor
              levels={categoryLevels}
              onChange={setCategoryLevels}
            />
          </Layout.Section>
        </Layout>

        {toastMessage && (
          <Toast
            content={toastMessage}
            onDismiss={() => setToastMessage(null)}
          />
        )}
      </Page>
    </>
  );
}
